import { FC, useContext, useMemo } from "react"
import cx from "classnames";
import { EcdColorsContext } from "../contexts/EcdColorsContext";
import { AdequacyBreakdownData, EcdTrendingArrowEnum } from "../interfaces";
import { getTrendingArrow } from "../utility";
import TooltipWrapper from "@app/shared/TooltipWrapper";

/**
 * Notes on the Directions and Colors of Arrows
 * Overall Gaps up is red, down is blue
 * New Gaps up is red, down is blue (but doesn't currently have an arrow)
 * Closed Gaps up is blue, down is red (but doesn't currently have an arrow)
 * Gap Percentage up is red, down is blue
 * Median Gap Age up is red, down is blue
 * County/Specialty Groups is always color-neutral
 */

interface AdequacyBreakdownTableProps {
  adequacyBreakdownData: AdequacyBreakdownData;
}

const AdequacyBreakdownTable: FC<AdequacyBreakdownTableProps> = ( props: AdequacyBreakdownTableProps ) => {
  const abd = props.adequacyBreakdownData;

  const bodyContent = useMemo(() => {
    if (!abd) {
      return null;
    }
    return (
      <dl className="qes-dashboard-tile__list">
        <LineItem
          title="Overall Gaps"
          value={abd.overallGapCount !== null ? parseFloat(abd.overallGapCount).toLocaleString() : "---"}
          valuePrior={abd.overallGapCountPrior !== null ? parseFloat(abd.overallGapCountPrior).toLocaleString() : "---"}
          trendArrow={abd.overallGapCountTrend}
        />
        <LineItem
          title="New Gaps"
          rowClassName="no-arrow"
          value={abd.newGaps !== null ? parseFloat(abd.newGaps).toLocaleString() : "---"}
          valuePrior={null}
          trendArrow={null}
        />
        <LineItem
          title="Closed Gaps"
          rowClassName="no-arrow"
          value={abd.closedGaps !== null ? parseFloat(abd.closedGaps).toLocaleString() : "---"}
          valuePrior={null}
          trendArrow={null}
        />
        <LineItem
          title="Gap Percentage"
          value={`${abd.gapPercent !== null ? abd.gapPercent?.toLocaleString() : "---"}${abd.gapPercent === null ? "" : "%"}`}
          valuePrior={`${abd.gapPercentPrior !== null ? abd.gapPercentPrior?.toLocaleString() : "---"}${abd.gapPercent === null ? "" : "%"}`}
          trendArrow={abd.gapPercentTrend}
        />
        <LineItem
          title="Median Gap Age Days"
          value={abd.medianGapAge !== null ? parseFloat(abd.medianGapAge).toLocaleString() : "---"}
          valuePrior={abd.medianGapAgePrior !== null ? parseFloat(abd.medianGapAgePrior).toLocaleString() : "---"}
          trendArrow={abd.medianGapAgeTrend}
        />
        <LineItem
          title="County/Specialty Groups"
          value={abd.countySpecialtyGroupCount !== null ? parseFloat(abd.countySpecialtyGroupCount).toLocaleString() : "---"}
          valuePrior={abd.countySpecialtyGroupCountPrior !== null ? parseFloat(abd.countySpecialtyGroupCountPrior).toLocaleString() : "---"}
          trendArrow={abd.countySpecialtyGroupCountTrend}
          rowClassName="summary"
        />
      </dl>
    );
  }, [props.adequacyBreakdownData]);

  return (
    <>
      {bodyContent}
    </>
  )
}

interface LineItemProps {
  clickHandler?: (title: string) => void;
  title: string;
  rowClassName?: string;
  trendArrow: EcdTrendingArrowEnum;
  value: string;
  valuePrior: string;
}

const LineItem: FC<LineItemProps> = (props: LineItemProps) => {
  const colorsContext = useContext(EcdColorsContext);
  const isNumber = !isNaN(parseFloat(props.value));
  return (
    <div
      className={cx({
        [props.rowClassName]: !!props.rowClassName,
        "clickable": !!props.clickHandler,
        "qes-dashboard-tile__line-item": true,
      })}
      onClick={() => !!props.clickHandler && props.clickHandler(props.title)}
    >
      <dt>{props.title}</dt>
      <dd>
        <span className="d-inline-flex space-between">
          <TooltipWrapper
            containerClassName="ms-2"
            position="right"
            text={
              !isNaN(parseFloat(props.valuePrior)) ? (
                <>Previous: {props.valuePrior}</>
              ) : null
            }
          >
            <span>{props.value}</span>
            {isNumber && getTrendingArrow(props.trendArrow, colorsContext)}
          </TooltipWrapper>
        </span>
      </dd>
    </div>
  );
}

export default AdequacyBreakdownTable;